import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  Button,
  Icon,
  Container,
  Text,
  Input,
  Slider,
  FormAlert,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import api from '../utils/axios';
import { isValidEmail } from '../utils/validators';
import { useTurnstile } from '../hooks/useTurnstile';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailTouched, setEmailTouched] = useState(false);
  const [buttonCooldown, setButtonCooldown] = useState(0);
  const { turnstileRef, token: turnstileToken, isReady: turnstileReady, isAvailable: turnstileAvailable, resetTurnstile } = useTurnstile({ action: 'forgot-password' });

  // Email validation function
  // Get email error message
  const getEmailError = (): string => {
    if (!emailTouched) return '';
    if (!email) return 'Email is required';
    if (!isValidEmail(email)) return 'Please enter a valid email address';
    return '';
  };

  // Cooldown timer effect for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Cooldown timer effect for button
  useEffect(() => {
    if (buttonCooldown > 0) {
      const timer = setTimeout(
        () => setButtonCooldown(buttonCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [buttonCooldown]);

  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark email as touched
    setEmailTouched(true);

    if (!email) {
      setError('Email address is required');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await api.post('/auth/forgot-password', {
        email,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      });
      setMessage(response.data.message);
      setEmailSent(true);
      setResendCooldown(60); // 60 second cooldown
      setButtonCooldown(60); // 60 second cooldown for button and input
    } catch (error: any) {
      let errMsg = 'Failed to send password reset email. Please try again.';

      if (error.response?.data?.detail) {
        errMsg = error.response.data.detail;
      } else if (error.message) {
        errMsg = error.message;
      }

      setError(errMsg);
    } finally {
      setIsLoading(false);
      resetTurnstile();
    }
  };

  const handleDismissError = () => {
    setError('');
  };

  const handleDismissMessage = () => {
    setMessage('');
  };

  const handleResendEmail = async () => {
    if (!email) {
      setError('Email address is required');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await api.post('/auth/forgot-password', {
        email,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      });
      setMessage('Password reset email resent successfully');
      setResendCooldown(60); // 60 second cooldown
      setButtonCooldown(60); // 60 second cooldown for button and input
    } catch (error: any) {
      const errMsg =
        error.response?.data?.detail || 'Failed to resend password reset email';
      setError(errMsg);
    } finally {
      setIsLoading(false);
      resetTurnstile();
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailTouched(true);

    // Clear messages when user types
    if (error) setError('');
    if (message) setMessage('');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen max-h-screen overflow-y-auto">
      <div className="hidden md:block col md:h-screen md:sticky md:top-0">
        <Container padding={'4xl'} className="h-full flex items-center justify-center bg-bg-secondary-alt">
          <Slider autoPlayInterval={5000} />
        </Container>
      </div>
      <div className="col">
        <div className="bg-bg-secondary-alt">
          <Container padding={'xl'} className="min-h-screen flex items-center justify-center bg-bg-tertiary">
            <Container padding={'4xl'} className="w-full max-w-md flex flex-col gap-bg-v2-xl rounded-bg-v2-md container-shadow-lg hover-scale-sm page-transition-enter">
              <div className="flex items-center justify-center gap-bg-v2-md animate-initial-hidden animate-fade-in-down animate-delay-200">
                <Icon name={IconName.BrandLogo} />
                <span
                  className="text-text-brand-primary font-semibold"
                  style={{
                    fontFamily: 'Inter',
                    fontSize: 'var(--font-size-display-sm, 30px)',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: 'var(--line-height-display-lg, 60px)',
                  }}
                >
                  BlueGenAI
                </span>
              </div>
              <Text variant={'display-xs'} weight="medium" className="font-bg-v2-display animate-initial-hidden animate-fade-in-up animate-delay-300" color="primary" align="center" as="p">
                Reset Your Password
              </Text>
              {message !== '' ? (
                <FormAlert
                  message={message}
                  variant="success"
                  onDismiss={handleDismissMessage}
                />
              ) : (
                <Text variant={'text-sm'} color="quaternary" align="center" className={'font-bg-v2-sans'} weight={'medium'} as="p">
                  Enter your email address and we'll send you a link to reset your password.
                </Text>
              )}
              <FormAlert
                message={error}
                variant="error"
                onDismiss={handleDismissError}
              />
              <form className="flex flex-col w-full gap-bg-v2-2xl" onSubmit={handleSendResetEmail} noValidate>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  label="Email Address"
                  placeholder="Enter your email address"
                  required
                  errorText={getEmailError()}
                  startIcon={<Icon name={IconName.Mail} size="md" />}
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => setEmailTouched(true)}
                  disabled={isLoading || buttonCooldown > 0}
                />
                  <div ref={turnstileRef} />
                  <div>
                    <Button
                      variant={'primary'}
                      type={'submit'}
                      loading={isLoading || !turnstileReady}
                      disabled={isLoading || !email || !isValidEmail(email) || buttonCooldown > 0 || !turnstileReady || (turnstileAvailable && !turnstileToken)}
                      className={'w-full flex justify-center items-center hover-lift'}
                      label={buttonCooldown > 0 ? `Resend in ${buttonCooldown}s` : 'Reset Password'}
                    />
                  </div>
                <div className="flex items-center justify-center gap-bg-v2-sm">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-bg-v2-xs text-bg-v2-sm font-bg-v2-medium text-bg-brand-solid hover:opacity-80 transition-smooth"
                  >
                    <Icon name={IconName.ArrowLeft} size="sm" />
                    Back to Login
                  </Link>
                </div>
                {emailSent && (
                  <div className="flex items-center justify-center gap-bg-v2-sm">
                    <Text variant={"text-sm"} color="secondary" align="center" className={"font-bg-v2-sans"} weight={"regular"} as="p">
                      Still having some trouble?
                      <a href="mailto:contact@bluegenai.com" className="font-bg-v2-sans font-bg-v2-medium text-bg-brand-solid hover:opacity-80 px-bg-v2-sm transition-smooth">Contact support</a>
                    </Text>
                </div>
                )}
                <div className="flex items-center justify-center gap-bg-v2-sm">
                  <Text variant={"text-sm"} color="secondary" align="center" className={"font-bg-v2-sans"} weight={"regular"} as="p">
                    Don't have an account?
                    <Link to="/signup" className="font-bg-v2-sans font-bg-v2-medium text-bg-brand-solid hover:opacity-80 px-bg-v2-sm transition-smooth">Sign up</Link>
                  </Text>
                </div>
              </form>
            </Container>
          </Container>
        </div>
      </div>
    </div>
  );
};

